/**
 * Hard-stop camera/microphone after a video/voice call.
 * Chrome's tab "recording" indicator stays on if any MediaStreamTrack is live.
 */
import {
  registerFromPublisher,
  stopAllRegisteredMedia,
} from "./mediaTrackRegistry";
import { getLiveCallMedia, clearLiveCallMedia } from "./callMediaHandle";

function stopTracksOnStream(stream) {
  if (!stream) return;
  try {
    if (typeof stream.getTracks === "function") {
      stream.getTracks().forEach((t) => {
        try {
          if (t && typeof t.stop === "function") {
            t.stop();
            t.enabled = false;
          }
        } catch (_) {
          /* ignore */
        }
      });
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} [opts]
 * @param {object} [opts.publisher] OpenTok publisher
 * @param {object} [opts.session] OpenTok session
 */
export function stopAllCallMedia(opts = {}) {
  const live = getLiveCallMedia();
  const publisher = opts.publisher || live.publisher;
  const session = opts.session || live.session;

  if (publisher) {
    // Mute first (helps OT release hardware on some browsers)
    try {
      publisher.publishVideo?.(false);
    } catch (_) {
      /* ignore */
    }
    try {
      publisher.publishAudio?.(false);
    } catch (_) {
      /* ignore */
    }

    // Ensure registry knows about any last stream
    try {
      registerFromPublisher(publisher);
    } catch (_) {
      /* ignore */
    }

    // Extract & stop tracks directly from DOM video nodes inside publisher
    try {
      const el = publisher.element || document.getElementById("publisher");
      if (el) {
        el.querySelectorAll?.("video, audio").forEach((mediaNode) => {
          stopTracksOnStream(mediaNode.srcObject);
          mediaNode.srcObject = null;
          try {
            mediaNode.pause?.();
          } catch (_) {
            /* ignore */
          }
        });
      }
    } catch (_) {
      /* ignore */
    }

    // Extract & stop tracks directly from publisher source objects
    try {
      const vSource = publisher.getVideoSource?.();
      if (vSource?.track && typeof vSource.track.stop === "function") {
        vSource.track.stop();
        vSource.track.enabled = false;
      }
      const aSource = publisher.getAudioSource?.();
      if (aSource?.track && typeof aSource.track.stop === "function") {
        aSource.track.stop();
        aSource.track.enabled = false;
      }
    } catch (_) {
      /* ignore */
    }

    // Extract & stop tracks from known OpenTok internal stream references
    try {
      const streams = [
        typeof publisher.getMediaStream === "function" ? publisher.getMediaStream() : null,
        publisher._?.webRtcStream,
        publisher._?.localMediaStream,
        publisher._?.mediaStream,
        publisher._?.stream?.getMediaStream?.(),
        publisher.stream?.getMediaStream?.(),
      ];
      streams.forEach((s) => stopTracksOnStream(s));
    } catch (_) {
      /* ignore */
    }

    try {
      session?.unpublish?.(publisher);
    } catch (_) {
      /* ignore */
    }
    try {
      if (typeof publisher.destroy === "function") publisher.destroy();
    } catch (_) {
      /* ignore */
    }
  }

  try {
    if (session && typeof session.disconnect === "function") {
      session.disconnect();
    }
  } catch (_) {
    /* ignore */
  }

  // Stop every track we ever registered + any remaining DOM media
  stopAllRegisteredMedia();
  clearLiveCallMedia();

  // OpenTok destroy() is async. Chrome keeps the tab recording indicator
  // until every MediaStreamTrack in this document is ended, so sweep again
  // after OT has had a chance to rebuild leftover publisher/subscriber tracks.
  if (typeof window !== "undefined") {
    [100, 400, 1200, 2500].forEach((ms) => {
      window.setTimeout(() => {
        try {
          stopAllRegisteredMedia();
        } catch (_) {
          /* ignore */
        }
      }, ms);
    });
  }
}

export default stopAllCallMedia;
